import { useState } from 'react';
import { X, Flag, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

const PRESET_REASONS = {
  user: [
    'Harassment or bullying',
    'Spam or fake account',
    'Inappropriate behavior',
    'Threatening messages',
    'Other',
  ],
  item: [
    'Misleading or false listing',
    'Inappropriate or offensive content',
    'Spam or scam',
    'Item does not exist',
    'Other',
  ],
  project: [
    'Misleading information',
    'Inappropriate content',
    'Spam or scam',
    'Other',
  ],
};

const TARGET_LABELS = { user: 'User', item: 'Item', project: 'Project' };

export default function ReportModal({ targetType, targetId, targetName, onClose }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const presets = PRESET_REASONS[targetType] || PRESET_REASONS.user;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) return;
    setSubmitting(true);
    try {
      await api.post('/reports', { target_type: targetType, target_id: targetId, reason: reason.trim() });
      toast.success('Report submitted. Our moderation team will review it.');
      onClose();
    } catch (err) {
      if (err.response?.status === 400) {
        toast.error('You have already submitted a report for this content.');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to submit report');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm" data-testid="report-modal">
        <div className="flex items-center justify-between p-5 border-b border-stone-100">
          <h3 className="font-poppins font-semibold text-gray-900 flex items-center gap-2">
            <Flag size={16} className="text-red-500" />
            Report {TARGET_LABELS[targetType]}
          </h3>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={16} className="text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <p className="text-sm text-gray-500 font-nunito">
            Reporting: <span className="font-semibold text-gray-800">{targetName}</span>
          </p>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 font-nunito">
              Select a reason
            </label>
            <div className="flex flex-wrap gap-2 mb-3">
              {presets.map(p => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setReason(p)}
                  className={`text-xs px-3 py-1.5 rounded-full border font-nunito font-semibold transition-all ${
                    reason === p
                      ? 'bg-red-500 text-white border-red-500'
                      : 'border-stone-200 text-gray-600 hover:border-red-300 hover:text-red-600'
                  }`}
                  data-testid={`report-preset-${p.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {p}
                </button>
              ))}
            </div>
            <textarea
              rows={3}
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Describe the issue in more detail (optional)..."
              data-testid="report-reason-input"
              className="w-full px-3 py-2.5 rounded-lg border border-stone-200 focus:border-red-400 focus:ring-2 focus:ring-red-400/20 outline-none font-nunito text-sm bg-white resize-none transition-all"
            />
          </div>

          <p className="text-xs text-gray-400 font-nunito">
            Reports are reviewed by our moderation team within 24–48 hours.
          </p>

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border-2 border-stone-200 text-gray-700 py-2.5 rounded-xl font-nunito font-semibold hover:bg-gray-50 transition-all text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !reason.trim()}
              data-testid="report-submit-btn"
              className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-xl font-nunito font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Flag size={14} />}
              {submitting ? 'Submitting…' : 'Submit Report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
